// Spec + source output -> document. TypeScript twin of MaxSpecMaterializer (PHP), used by POS
// to render dashboards from live queries and stored snapshots. Whatever the sources return,
// the result validates: a node that cannot be filled becomes a notice, never an error.
import { ruleMatches, type RuleOp } from './rules.ts'
import { CONTAINER_TYPES, validateDocument, type MaxDocument } from './document.ts'
import { LIVE_SOURCES, BOUND_ITEM_KINDS, type MaxSpec, type Period, type SpecSource } from './spec.ts'

export const MAX_TABLE_ROWS = 2000
const MAX_CATEGORIES = 1000
const MAX_ITEMS: Record<string, number> = { donut: 24, funnel: 12, treemap: 50 }
const MAX_SPARKLINE = 120

export interface MaterializeMessages {
  noData: string
  partFailed: string
}

export interface MaterializeOptions {
  now?: Date
  messages?: Partial<MaterializeMessages>
}

const DEFAULT_MESSAGES: MaterializeMessages = {
  noData: 'No data for this period.',
  partFailed: 'This part of the report could not be displayed.'
}

type Data = Record<string, unknown>
type Node = Record<string, unknown>
interface BindingLike { source: string, path: string, where?: { field: string, op: RuleOp, value?: unknown }[], sort?: { key: string, dir?: string }, limit?: number, field?: string }

// ---------------------------------------------------------------------------
// Periods and live sources

const pad = (n: number) => String(n).padStart(2, '0')
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)

/** Inclusive Y-m-d dates in local time, like MaxSpecExecutor::resolvePeriod. all_time has no start. */
export function resolvePeriod (period: Period, now: Date = new Date()): { from: string | null, to: string } {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const monday = addDays(today, -((today.getDay() + 6) % 7))
  switch (period) {
    case 'today': return { from: ymd(today), to: ymd(today) }
    case 'yesterday': return { from: ymd(addDays(today, -1)), to: ymd(addDays(today, -1)) }
    case 'last_7_days': return { from: ymd(addDays(today, -7)), to: ymd(addDays(today, -1)) }
    case 'last_30_days': return { from: ymd(addDays(today, -30)), to: ymd(addDays(today, -1)) }
    case 'this_week': return { from: ymd(monday), to: ymd(today) }
    case 'last_week': return { from: ymd(addDays(monday, -7)), to: ymd(addDays(monday, -1)) }
    case 'this_month': return { from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), to: ymd(today) }
    case 'last_month': return {
      from: ymd(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      to: ymd(new Date(today.getFullYear(), today.getMonth(), 0))
    }
    case 'this_year': return { from: `${today.getFullYear()}-01-01`, to: ymd(today) }
    case 'last_year': return { from: `${today.getFullYear() - 1}-01-01`, to: `${today.getFullYear() - 1}-12-31` }
    case 'all_time': return { from: null, to: ymd(today) }
  }
}

export function sourcePeriod (spec: MaxSpec, source: SpecSource): Period {
  return source.period ?? spec.period ?? 'yesterday'
}

/** Endpoint and query for a live source; the host adds the shop and its own credentials. */
export function liveRequest (spec: MaxSpec, source: SpecSource): { path: string, query: Record<string, string> } | null {
  const def = source.live !== undefined ? LIVE_SOURCES[source.live] : undefined
  if (!def) return null
  const query: Record<string, string> = { period: sourcePeriod(spec, source) }
  if (def.limit) query.limit = String(source.limit ?? 10)
  return { path: `reports/${def.endpoint}.json`, query }
}

/** Shape raw live rows as { rows, count, totals } so specs can bind totals.total and rows. */
export function liveOutput (raw: unknown): Data {
  const rows = Array.isArray(raw) ? raw.filter(r => r && typeof r === 'object') as Data[] : []
  const totals: Record<string, number> = {}
  for (const key of ['total', 'vat', 'count']) {
    let sum = 0
    let seen = false
    for (const row of rows) {
      const n = toNumber(row[key])
      if (n !== null) { sum += n; seen = true }
    }
    if (seen) totals[key] = Math.round(sum * 100) / 100
  }
  return { rows, count: rows.length, totals }
}

/** Same spec with a different default period; sources that pin their own period keep it. */
export function withPeriod (spec: MaxSpec, period: Period): MaxSpec {
  return { ...spec, period }
}

// ---------------------------------------------------------------------------
// Values

function toNumber (v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return null
}

function isBinding (v: unknown): v is BindingLike {
  return !!v && typeof v === 'object' && !Array.isArray(v) && typeof (v as BindingLike).source === 'string' && typeof (v as BindingLike).path === 'string'
}

export function getPath (value: unknown, path: string): unknown {
  let current = value
  for (const segment of path.split('.')) {
    if (current === null || typeof current !== 'object' || !(segment in (current as Data))) return null
    current = (current as Data)[segment]
  }
  return current === undefined ? null : current
}

function resolve (binding: BindingLike, data: Record<string, Data>): unknown {
  return data[binding.source] !== undefined ? getPath(data[binding.source], binding.path) : null
}

/** Report output is full of numeric strings from the database; documents want numbers. */
function scalar (v: unknown, maxLength = 500): string | number | boolean | null {
  if (typeof v === 'boolean' || v === null || v === undefined) return v ?? null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string') {
    const t = v.trim()
    if (t !== '' && Number.isFinite(Number(t)) && !/^0[0-9]/.test(t)) return Number(t)
    return v.slice(0, maxLength)
  }
  return null
}

function rows (binding: BindingLike, data: Record<string, Data>, max: number): Data[] {
  const list = resolve(binding, data)
  if (!Array.isArray(list)) return []
  const where = binding.where ?? []
  // where keeps the rows matching every filter; a filter field may be a dotted path in the row
  const out = (list.filter(r => r && typeof r === 'object' && !Array.isArray(r)) as Data[])
    .filter(r => where.every(f => ruleMatches({ field: 'v', op: f.op, value: f.value as never, intent: 'neutral' }, { v: getPath(r, f.field) })))
  if (binding.sort?.key) {
    const key = binding.sort.key
    const desc = binding.sort.dir === 'desc'
    out.sort((a, b) => {
      const va = getPath(a, key)
      const vb = getPath(b, key)
      const na = toNumber(va)
      const nb = toNumber(vb)
      const cmp = na !== null && nb !== null ? na - nb : String(va ?? '').localeCompare(String(vb ?? ''))
      return desc ? -cmp : cmp
    })
  }
  return out.slice(0, binding.limit ? Math.min(binding.limit, max) : max)
}

function currency (value: unknown, data: Record<string, Data>): string | null {
  const v = isBinding(value) ? resolve(value, data) : value
  return typeof v === 'string' && /^[A-Z]{3}$/.test(v) ? v : null
}

// ---------------------------------------------------------------------------
// Nodes

function notice (text: string, title?: string): Node {
  const node: Node = { type: 'callout', intent: 'neutral', text }
  if (title) node.title = title.slice(0, 120)
  return node
}

function nodes (list: unknown, data: Record<string, Data>, msg: MaterializeMessages): Node[] {
  if (!Array.isArray(list)) return []
  const out: Node[] = []
  for (const raw of list) {
    const node = materializeNode({ ...(raw as Node) }, data, msg)
    if (node) out.push(node)
  }
  return out
}

function materializeNode (node: Node, data: Record<string, Data>, msg: MaterializeMessages): Node | null {
  if (node.when !== undefined) {
    const when = node.when as BindingLike & { op: RuleOp, value?: unknown }
    const actual = resolve(when, data)
    if (!ruleMatches({ field: 'v', op: when.op, value: when.value as never, intent: 'neutral' }, { v: actual })) return null
    delete node.when
  }
  switch (node.type) {
    case 'metric': return metric(node, data)
    case 'table': return table(node, data)
    case 'chart': return chart(node, data, msg)
  }
  if ((CONTAINER_TYPES as readonly string[]).includes(node.type as string) && Array.isArray(node.children)) {
    const children = nodes(node.children, data, msg)
    if (!children.length) return null
    if (node.type === 'split' && children.length !== 2) return { type: 'stack', children }
    if (node.type === 'tabs' && children.length < 2) return children[0] ?? null
    node.children = children
  }
  return node
}

function metric (node: Node, data: Record<string, Data>): Node {
  if (isBinding(node.value)) {
    const value = scalar(resolve(node.value, data), 40)
    node.value = typeof value === 'boolean' ? null : value
  }
  if (node.currency !== undefined) {
    const c = currency(node.currency, data)
    if (c === null) delete node.currency
    else node.currency = c
  }
  const delta = node.delta as { value?: unknown } | undefined
  if (delta && isBinding(delta.value)) {
    const n = toNumber(resolve(delta.value, data))
    if (n === null) delete node.delta
    else node.delta = { ...delta, value: n }
  }
  if (isBinding(node.sparkline)) {
    const binding = node.sparkline
    const list = resolve(binding, data)
    const limit = binding.limit ? Math.min(binding.limit, MAX_SPARKLINE) : MAX_SPARKLINE
    const points = Array.isArray(list)
      ? list.slice(0, limit).map(item => toNumber(binding.field && item && typeof item === 'object' ? getPath(item, binding.field) : item))
      : []
    if (points.length) node.sparkline = points
    else delete node.sparkline
  }
  return node
}

function table (node: Node, data: Record<string, Data>): Node {
  const columns = (node.columns as Node[]).map(column => {
    const c: Node = { ...column }
    if (c.currency !== undefined) {
      const code = currency(c.currency, data)
      if (code === null) delete c.currency
      else c.currency = code
    }
    return c
  })
  if (isBinding(node.rows)) {
    node.rows = rows(node.rows, data, MAX_TABLE_ROWS).map(source => {
      const row: Data = {}
      for (const column of columns) {
        row[column.key as string] = scalar(getPath(source, (column.field ?? column.key) as string))
      }
      return row
    })
  }
  for (const column of columns) delete column.field
  node.columns = columns
  return node
}

function chart (node: Node, data: Record<string, Data>, msg: MaterializeMessages): Node {
  const spec = node.chart as Node & { data?: unknown, kind: string }
  if (!isBinding(spec.data)) return node
  const itemKind = (BOUND_ITEM_KINDS as readonly string[]).includes(spec.kind)
  const list = rows(spec.data, data, itemKind ? MAX_ITEMS[spec.kind] ?? MAX_CATEGORIES : MAX_CATEGORIES)
  const { data: _binding, ...rest } = spec
  const out: Node = { ...rest }
  const empty = () => notice(msg.noData, typeof node.title === 'string' ? node.title : undefined)
  if (itemKind) {
    const items: Node[] = []
    for (const row of list) {
      const value = toNumber(getPath(row, spec.value as string))
      const label = getPath(row, spec.label as string)
      if (value === null || value < 0 || label === null || typeof label === 'object' || String(label).trim() === '') continue
      items.push({ label: String(label).slice(0, 80), value })
    }
    delete out.label
    delete out.value
    if (!items.length) return empty()
    out.items = items
  } else {
    if (!list.length) return empty()
    out.categories = list.map(row => {
      const label = getPath(row, spec.category as string)
      return label === null || typeof label === 'object' ? '' : String(label).slice(0, 80)
    })
    out.series = (spec.series as Node[]).map(def => {
      const { field, ...s } = def
      return { ...s, data: list.map(row => toNumber(getPath(row, field as string))) }
    })
    delete out.category
  }
  node.chart = out
  return node
}

function nodeIsValid (doc: MaxDocument, node: Node): boolean {
  return validateDocument({ ...doc, body: [node] }).valid
}

function degrade (doc: MaxDocument, list: Node[], msg: MaterializeMessages): Node[] {
  return list.map(node => {
    if (nodeIsValid(doc, node)) return node
    if ((CONTAINER_TYPES as readonly string[]).includes(node.type as string) && Array.isArray(node.children)) {
      const repaired = { ...node, children: degrade(doc, node.children as Node[], msg) }
      if (nodeIsValid(doc, repaired)) return repaired
    }
    return notice(msg.partFailed)
  })
}

/**
 * @param data  source id -> output (report JSON, or liveOutput() of a live query). Missing
 *              sources leave their bindings empty.
 */
export function materializeSpec (spec: MaxSpec, data: Record<string, unknown>, options: MaterializeOptions = {}): MaxDocument {
  const msg = { ...DEFAULT_MESSAGES, ...options.messages }
  const now = options.now ?? new Date()
  const normalized: Record<string, Data> = {}
  for (const [id, output] of Object.entries(data)) {
    if (output !== undefined && output !== null) normalized[id] = JSON.parse(JSON.stringify(output))
  }
  let body = nodes(spec.body, normalized, msg)
  if (!body.length) body = [notice(msg.noData)]
  const doc: Record<string, unknown> = {
    version: 1,
    kind: spec.kind,
    title: spec.title,
    locale: spec.locale,
    generatedAt: now.toISOString(),
    body
  }
  if (spec.preset) doc.preset = spec.preset
  if (spec.period) {
    const { from, to } = resolvePeriod(spec.period, now)
    doc.period = from ? { from, to } : { to }
  }
  const typed = doc as unknown as MaxDocument
  if (!validateDocument(typed).valid) typed.body = degrade(typed, body, msg) as unknown as MaxDocument['body']
  return typed
}
